import type { FreightHazmatInfo, LtlCommodity } from '../types/ltl';

export const RequiredHazmatFields = {
  identificationNumber: 'identificationNumber',
  properShippingName: 'properShippingName',
  hazardClass: 'hazardClass',
  packingGroup: 'packingGroup',
  emergencyContactName: 'emergencyContactName',
  emergencyPhone: 'emergencyPhone',
} as const;

export type RequiredHazmatField = (typeof RequiredHazmatFields)[keyof typeof RequiredHazmatFields];

const isRequiredFieldFilled = (info: FreightHazmatInfo, field: RequiredHazmatField): boolean =>
  field === 'hazardClass' ? !!info.hazardClass?.length : !!info[field];

/**
 * Returns true when any hazmat field (required or not) has a meaningful value.
 * `flashpointTemperatureF` uses != null rather than !! so that 0 °F counts as filled.
 * Used to determine when required-field validation should activate.
 */
export const isAnyHazmatFieldFilled = (info: FreightHazmatInfo | null): boolean =>
  !!info &&
  !!(
    info.identificationNumber ||
    info.properShippingName ||
    info.hazardClass?.length ||
    info.packingGroup ||
    info.emergencyContactName ||
    info.emergencyPhone ||
    info.emergencyResponseReference ||
    info.flashpointTemperatureF != null ||
    info.additionalDetails
  );

export const getMissingRequiredHazmatFields = (info: FreightHazmatInfo | null): Set<RequiredHazmatField> => {
  if (!isAnyHazmatFieldFilled(info)) return new Set();
  return new Set(Object.values(RequiredHazmatFields).filter((f) => !isRequiredFieldFilled(info!, f)));
};

export const isHazmatReadyForQuote = (info: FreightHazmatInfo | null): boolean => getMissingRequiredHazmatFields(info).size === 0;

/**
 * Unlike getMissingRequiredHazmatFields, keys off the explicit `isHazmat` flag — no validation when false.
 * When `isHazmat` is true but `hazmatInfo` is null (e.g. right after the checkbox is ticked, or a saved
 * plan stored `isHazmat: true` with a null info), all six required fields are considered missing.
 */
export const getMissingRequiredHazmatFieldsForCommodity = (
  commodity: Pick<LtlCommodity, 'isHazmat' | 'hazmatInfo'>,
): Set<RequiredHazmatField> => {
  if (!commodity.isHazmat) return new Set();
  if (!commodity.hazmatInfo) return new Set(Object.values(RequiredHazmatFields));
  return new Set(Object.values(RequiredHazmatFields).filter((f) => !isRequiredFieldFilled(commodity.hazmatInfo!, f)));
};

export const areAllCommoditiesHazmatReady = (handlingUnits: { commodities: Pick<LtlCommodity, 'isHazmat' | 'hazmatInfo'>[] }[]): boolean =>
  handlingUnits.every((u) => u.commodities.every((c) => getMissingRequiredHazmatFieldsForCommodity(c).size === 0));
